import {makeObservable, observable, action} from 'mobx';
import {requestManager} from '@/utils/ky-hooks/requestManager';
import {DefaultFilter, ColdStartProgressData, EvolveProgressData} from '@/dicts/taskRuning';
import type {TaskRunningStore} from '../TaskRunningStore';

/**
 * 任务初始化与清理 Store
 *
 * 职责:
 * - 管理任务初始化状态标记
 * - 处理任务切换时的初始化逻辑
 * - 管理请求取消
 * - 重置相关 Store 状态
 *
 * 设计原则:
 * - 单一职责: 只管理初始化和清理相关逻辑
 * - 解耦: 通过依赖注入访问其他 Store，避免循环依赖
 */
export class TaskInitializationStore {
    /**
     * 是否已初始化标记
     * 用于防止重复初始化同一个任务
     */
    hasInitialized = false;

    constructor() {
        makeObservable(this, {
            hasInitialized: observable,
            reset: action,
            setHasInitialized: action
        });
    }

    /**
     * 重置初始化标记
     * 用于强制重新初始化（如需要刷新数据时）
     */
    reset() {
        this.hasInitialized = false;
    }

    /**
     * 设置初始化标记
     */
    setHasInitialized(value: boolean) {
        this.hasInitialized = value;
    }

    /**
     * 初始化任务运行 Store
     * 处理任务切换时的状态重置和数据初始化
     *
     * @param mainStore - TaskRunningStore 主实例
     * @param job_id - 任务ID
     */
    initTaskRunningStore(mainStore: TaskRunningStore, job_id: string) {
        // 停止轮询
        mainStore.pollingService.stop();
        mainStore.isPolling = false;

        // 如果切换到不同的任务，取消旧任务的所有请求
        if (mainStore.job_id !== job_id) {
            this.cancelAllJobRequests(mainStore.job_id);
        }

        // 如果已初始化且是同一个任务，直接返回
        if (this.hasInitialized && mainStore.job_id === job_id) {
            return;
        }

        // 更新 job_id
        mainStore.job_id = job_id;
        this.hasInitialized = true;

        // 重置子 Store
        mainStore.taskExecutionStore.reset();
        mainStore.taskExecutionStore.setJobId(job_id);
        mainStore.operationLoadingStore.reset();
        mainStore.confirmDialogStore.reset();
        mainStore.viewStateStore.reset();

        // 重置并初始化 TaskEvoRunStore
        mainStore.taskEvoRunStore.reset();
        mainStore.taskEvoRunStore.job_id = job_id;
        mainStore.taskEvoRunStore.updateSelectedJobDetail(
            mainStore.jobsListStore?.selectedJobDetail || null
        );

        // 重置阶段信息
        mainStore.stageInfo = null;
        mainStore.viewStateStore.setStageInfoName('');
        mainStore.setSelectedStageRowInfo(null);

        // 重置 WebIDE 链接和报告链接
        mainStore.real_web_ide_url = '';
        mainStore.web_ide_url = '';
        mainStore.report_url = '';

        // 初始化阶段数据
        this.initTaskRunningStageData(mainStore);

        // 重置进度数据
        mainStore.coldStartProgressData = ColdStartProgressData;
        mainStore.evolveProgressData = EvolveProgressData;
        mainStore.allProgressInfo = [];
    }

    /**
     * 取消任务的所有进行中请求
     * 用于任务删除或切换时，取消所有已发出但未返回的 HTTP 请求
     *
     * @param jobIds - 任务ID（字符串或数组）
     */
    cancelAllJobRequests(jobIds?: string | string[]) {
        // 如果没有传入参数，直接返回
        if (!jobIds) {
            return;
        }

        // 根据类型调用不同的取消方法
        if (Array.isArray(jobIds)) {
            // 数组：精确匹配多个 key
            requestManager.cancelByKeys(jobIds);
        } else {
            // 字符串：模糊匹配（包含该 job_id 的所有请求）
            requestManager.cancelByPattern(jobIds);
        }
    }

    /**
     * 初始化任务执行结果数据
     * 清理解信息列表、重置阶段信息和筛选参数
     *
     * @param mainStore - TaskRunningStore 主实例
     */
    initTaskRunningStageData(mainStore: TaskRunningStore) {
        // 清理解信息列表
        mainStore.initialSolutionFileInfoList = [];
        mainStore.evolveSelectedIslands = [];

        // 重置阶段详细结果信息
        mainStore.stageInfo = null;

        // 重置筛选参数
        mainStore.filter = DefaultFilter;

        // 清空 StageDataCache
        mainStore.stageDataCache.reset();
    }
}
